import React from "react";
import { Plus, Search, Shield } from "lucide-react";
import { apiFetch } from "../../../utils/apiFetch";
import { API_URL, FONT, COUNTRY_CONFIG } from "../constants";
import { C } from "../styles";
import { authHeaders } from "../helpers";
import type { PayType, DeductionType, CountryCode } from "../types";
import PayTypesTable from "../components/PayTypesTable";
import PayTypeDrawer, { Draft, DrawerCategory, DrawerMode } from "../components/PayTypeDrawer";
import DeleteConfirmModal from "../components/DeleteConfirmModal";

interface Props {
  businessCountry?: CountryCode | string;
}

export default function PayTypesSection({ businessCountry = "CA" }: Props) {
  const [payTypes, setPayTypes] = React.useState<PayType[]>([]);
  const [deductions, setDeductions] = React.useState<DeductionType[]>([]);
  const [companyProvince, setCompanyProvince] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [activeTab, setActiveTab] = React.useState<"earnings" | "deductions">("earnings");
  const [search, setSearch] = React.useState("");

  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [drawerMode, setDrawerMode] = React.useState<DrawerMode>("add");
  const [drawerCategory, setDrawerCategory] = React.useState<DrawerCategory>("earning");
  const [editingId, setEditingId] = React.useState<string | number | null>(null);
  const [draft, setDraft] = React.useState<Draft>({});
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);
  const [editingIsDefault, setEditingIsDefault] = React.useState(false);
  const [kebabOpenId, setKebabOpenId] = React.useState<string | number | null>(null);

  React.useEffect(() => {
    async function load() {
      try {
        const [ptRes, dtRes, cpRes] = await Promise.all([
          apiFetch("/api/v1/pay-types", { headers: authHeaders() }),
          apiFetch("/api/v1/deduction-types", { headers: authHeaders() }),
          apiFetch("/api/v1/company/profile", { headers: authHeaders() }),
        ]);
        if (ptRes.ok) setPayTypes(await ptRes.json());
        if (dtRes.ok) setDeductions(await dtRes.json());
        if (cpRes.ok) {
          const cp = await cpRes.json();
          setCompanyProvince(cp.province_state || cp.province || "");
        }
      } catch (err) {
        console.error("Failed to load pay types", err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  React.useEffect(() => {
    if (!kebabOpenId) return;
    const handleClick = () => setKebabOpenId(null);
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, [kebabOpenId]);

  const businessCountryUpper = (String(businessCountry) || "CA").toUpperCase();
  const companyConfig = (COUNTRY_CONFIG as any)[businessCountryUpper] || (COUNTRY_CONFIG as any).CA;

  const filterFn = (item: any) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (item.name || "").toLowerCase().includes(q) || (item.description || "").toLowerCase().includes(q);
  };
  const filteredPayTypes = payTypes.filter(filterFn);
  const filteredDeductions = deductions.filter(filterFn);

  const defaultPayTypeCount = payTypes.filter((p: any) => p.is_default).length;
  const customPayTypeCount = payTypes.length - defaultPayTypeCount;
  const defaultDeductionCount = deductions.filter((d: any) => d.is_default).length;
  const customDeductionCount = deductions.length - defaultDeductionCount;

  function openAddDrawer(category: DrawerCategory) {
    setDrawerCategory(category);
    setDrawerMode("add");
    setEditingId(null);
    setEditingIsDefault(false);
    setDraft({
      name: "", description: "", calc_method: "fixed", default_rate: "", unit_label: "",
      federal_taxable: true, cpp_contributable: true, ei_insurable: true, vacationable: true, wcb_reportable: true,
      t4_box: "14", is_pre_tax: false, employer_matched: false, default_amount: "",
    });
    setSaveError(null);
    setDrawerOpen(true);
  }

  function openEditDrawer(item: any, category: DrawerCategory) {
    setDrawerCategory(category);
    setDrawerMode("edit");
    setEditingId(item.id);
    setEditingIsDefault(!!item.is_default);
    setDraft({
      name: item.name || "", description: item.description || "", calc_method: item.calc_method || "fixed",
      default_rate: item.default_rate != null ? String(item.default_rate) : "",
      default_amount: item.default_amount != null ? String(item.default_amount) : "",
      unit_label: item.unit_label || "",
      federal_taxable: item.federal_taxable !== undefined ? item.federal_taxable : true,
      cpp_contributable: item.cpp_contributable !== undefined ? item.cpp_contributable : true,
      ei_insurable: item.ei_insurable !== undefined ? item.ei_insurable : true,
      vacationable: item.vacationable !== undefined ? item.vacationable : true,
      wcb_reportable: item.wcb_reportable !== undefined ? item.wcb_reportable : true,
      t4_box: item.t4_box || "14",
      is_pre_tax: item.is_pre_tax || false,
      employer_matched: item.employer_matched || false,
    });
    setSaveError(null);
    setDrawerOpen(true);
  }

  function closeDrawer() {
    setDrawerOpen(false);
    setEditingId(null);
    setDraft({});
    setSaveError(null);
  }

  async function onSave() {
    if (!draft.name || !draft.name.trim()) { setSaveError("Name is required"); return; }
    setSaving(true); setSaveError(null);
    const isEarning = drawerCategory === "earning";
    const endpoint = isEarning ? "/api/v1/pay-types" : "/api/v1/deduction-types";
    const url = drawerMode === "add" ? endpoint : endpoint + "/" + editingId;
    const method = drawerMode === "add" ? "POST" : "PATCH";
    const body: any = {
      name: draft.name, description: draft.description || null,
      calc_method: draft.calc_method, unit_label: draft.unit_label || null,
    };
    if (isEarning) {
      body.default_rate = draft.default_rate ? Number(draft.default_rate) : null;
      body.federal_taxable = draft.federal_taxable;
      body.cpp_contributable = draft.cpp_contributable;
      body.ei_insurable = draft.ei_insurable;
      body.vacationable = draft.vacationable;
      body.wcb_reportable = draft.wcb_reportable;
      body.t4_box = draft.t4_box || null;
    } else {
      body.default_amount = draft.default_amount ? Number(draft.default_amount) : null;
      body.is_pre_tax = draft.is_pre_tax;
      body.employer_matched = draft.employer_matched;
    }
    try {
      const res = await fetch(API_URL + url, { method, headers: authHeaders(), body: JSON.stringify(body) });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        setSaveError(errData.detail || "Save failed (status " + res.status + ")");
        setSaving(false); return;
      }
      const saved = await res.json();
      if (isEarning) {
        setPayTypes((prev) => drawerMode === "add" ? prev.concat([saved]) : prev.map((p: any) => p.id === saved.id ? saved : p));
      } else {
        setDeductions((prev) => drawerMode === "add" ? prev.concat([saved]) : prev.map((d: any) => d.id === saved.id ? saved : d));
      }
      closeDrawer();
    } catch (err: any) {
      setSaveError("Network error: " + err.message);
    } finally {
      setSaving(false);
    }
  }

  async function onDelete() {
    if (!editingId) return;
    setDeleting(true);
    const isEarning = drawerCategory === "earning";
    const endpoint = isEarning ? "/api/v1/pay-types/" : "/api/v1/deduction-types/";
    try {
      const res = await fetch(API_URL + endpoint + editingId, { method: "DELETE", headers: authHeaders() });
      if (!res.ok && res.status !== 204) {
        const errData = await res.json().catch(() => ({}));
        setSaveError(errData.detail || "Delete failed");
        setDeleting(false); setConfirmDelete(false); return;
      }
      if (isEarning) setPayTypes((prev) => prev.filter((p: any) => p.id !== editingId));
      else setDeductions((prev) => prev.filter((d: any) => d.id !== editingId));
      setConfirmDelete(false); closeDrawer();
    } catch (err: any) {
      setSaveError("Network error: " + err.message);
    } finally {
      setDeleting(false);
    }
  }

  function onDuplicate(item: any, category: DrawerCategory) {
    setDrawerCategory(category);
    setDrawerMode("add");
    setEditingId(null);
    setEditingIsDefault(false);
    setDraft({
      name: (item.name || "") + " (copy)", description: item.description || "",
      calc_method: item.calc_method || "fixed",
      default_rate: item.default_rate != null ? String(item.default_rate) : "",
      default_amount: item.default_amount != null ? String(item.default_amount) : "",
      unit_label: item.unit_label || "",
      federal_taxable: item.federal_taxable !== undefined ? item.federal_taxable : true,
      cpp_contributable: item.cpp_contributable !== undefined ? item.cpp_contributable : true,
      ei_insurable: item.ei_insurable !== undefined ? item.ei_insurable : true,
      vacationable: item.vacationable !== undefined ? item.vacationable : true,
      wcb_reportable: item.wcb_reportable !== undefined ? item.wcb_reportable : true,
      t4_box: item.t4_box || "14",
      is_pre_tax: item.is_pre_tax || false,
      employer_matched: item.employer_matched || false,
    });
    setSaveError(null); setKebabOpenId(null); setDrawerOpen(true);
  }

  async function onToggleActive(item: any, category: DrawerCategory) {
    const isEarning = category === "earning";
    const endpoint = isEarning ? "/api/v1/pay-types/" : "/api/v1/deduction-types/";
    setKebabOpenId(null);
    try {
      const res = await fetch(API_URL + endpoint + item.id, { method: "PATCH", headers: authHeaders(), body: JSON.stringify({ is_active: !item.is_active }) });
      if (res.ok) {
        const updated = await res.json();
        if (isEarning) setPayTypes((prev) => prev.map((p: any) => p.id === updated.id ? updated : p));
        else setDeductions((prev) => prev.map((d: any) => d.id === updated.id ? updated : d));
      }
    } catch (err) {
      console.error("Toggle active failed", err);
    }
  }

  function onKebabDelete(item: any, category: DrawerCategory) {
    setDrawerCategory(category);
    setEditingId(item.id);
    setEditingIsDefault(!!item.is_default);
    setDraft({ name: item.name });
    setKebabOpenId(null);
    setConfirmDelete(true);
  }

  if (loading) {
    return <div style={{ padding: 60, textAlign: "center", color: (C as any).muted, fontSize: 13 }}>Loading pay types...</div>;
  }

  const category: DrawerCategory = activeTab === "earnings" ? "earning" : "deduction";

  return (
    <div>
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 24, marginBottom: 20 }}>
        <div style={{ flex: 1, maxWidth: 560 }}>
          <h1 style={{ fontSize: 26, fontWeight: 700, color: C.ink, letterSpacing: "-0.015em", margin: 0, marginBottom: 6 }}>Pay types &amp; deductions</h1>
          <p style={{ fontSize: 14, color: C.ink, lineHeight: 1.55, fontWeight: 500, margin: 0 }}>Define the earnings and deductions that drive every pay run.</p>
        </div>
        <button onClick={() => openAddDrawer(category)} style={{ background: C.ink, color: "#fff", border: 0, borderRadius: 8, padding: "10px 16px", fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: FONT, display: "inline-flex", alignItems: "center", gap: 7, whiteSpace: "nowrap" }}>
          <Plus size={13} strokeWidth={2.2} />
          New item
        </button>
      </div>

      <div style={{ fontSize: 13, color: C.ink, paddingBottom: 16, marginBottom: 20, borderBottom: "1.5px solid " + C.ink }}>
        <span style={{ fontWeight: 500 }}>Configured for </span>
        <strong style={{ fontWeight: 700 }}>{companyConfig.name}</strong>
        {companyProvince && <span> &middot; {companyProvince}</span>}
        <span> &middot; CRA payroll rules apply</span>
      </div>

      <div style={{ display: "flex", gap: 48, marginBottom: 32, padding: "8px 0" }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.ink, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 6 }}>Earnings</div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
            <span style={{ fontSize: 32, fontWeight: 700, color: C.ink, letterSpacing: "-0.02em", fontVariantNumeric: "tabular-nums" }}>{payTypes.length}</span>
            <span style={{ fontSize: 12, color: C.ink, fontWeight: 500 }}>{defaultPayTypeCount} default, {customPayTypeCount} custom</span>
          </div>
        </div>
        <div style={{ width: 1, background: C.line }} />
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.ink, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 6 }}>Deductions</div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
            <span style={{ fontSize: 32, fontWeight: 700, color: C.ink, letterSpacing: "-0.02em", fontVariantNumeric: "tabular-nums" }}>{deductions.length}</span>
            <span style={{ fontSize: 12, color: C.ink, fontWeight: 500 }}>{defaultDeductionCount} default, {customDeductionCount} custom</span>
          </div>
        </div>
        <div style={{ width: 1, background: C.line }} />
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.ink, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 6 }}>In use</div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
            <span style={{ fontSize: 32, fontWeight: 700, color: C.ink, letterSpacing: "-0.02em", fontVariantNumeric: "tabular-nums" }}>{payTypes.filter((p: any) => p.is_active).length + deductions.filter((d: any) => d.is_active).length}</span>
            <span style={{ fontSize: 12, color: C.ink, fontWeight: 500 }}>across employees</span>
          </div>
        </div>
      </div>

      <div style={{ display: "inline-flex", background: (C as any).surface2 || "#F4F6F8", borderRadius: 8, padding: 3, marginBottom: 20 }}>
        <button onClick={() => setActiveTab("earnings")} style={{ padding: "7px 16px", fontSize: 13, fontWeight: activeTab === "earnings" ? 600 : 500, color: activeTab === "earnings" ? C.ink : (C as any).muted, cursor: "pointer", borderRadius: 6, fontFamily: FONT, background: activeTab === "earnings" ? "#fff" : "transparent", border: 0, display: "inline-flex", alignItems: "center", gap: 7, boxShadow: activeTab === "earnings" ? "0 1px 2px rgba(10,26,30,.08)" : "none" }}>
          Earnings <span style={{ fontSize: 11.5, fontWeight: 600, color: activeTab === "earnings" ? (C as any).muted : (C as any).faint, background: (C as any).surface2 || "#F4F6F8", padding: "1px 7px", borderRadius: 10 }}>{payTypes.length}</span>
        </button>
        <button onClick={() => setActiveTab("deductions")} style={{ padding: "7px 16px", fontSize: 13, fontWeight: activeTab === "deductions" ? 600 : 500, color: activeTab === "deductions" ? C.ink : (C as any).muted, cursor: "pointer", borderRadius: 6, fontFamily: FONT, background: activeTab === "deductions" ? "#fff" : "transparent", border: 0, display: "inline-flex", alignItems: "center", gap: 7, boxShadow: activeTab === "deductions" ? "0 1px 2px rgba(10,26,30,.08)" : "none" }}>
          Deductions <span style={{ fontSize: 11.5, fontWeight: 600, color: activeTab === "deductions" ? (C as any).muted : (C as any).faint, background: (C as any).surface2 || "#F4F6F8", padding: "1px 7px", borderRadius: 10 }}>{deductions.length}</span>
        </button>
      </div>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, gap: 14 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: C.ink }}>All {activeTab === "earnings" ? "earnings" : "deductions"}</span>
          <span style={{ fontSize: 12, color: C.ink, fontWeight: 500, fontVariantNumeric: "tabular-nums" }}>
            {activeTab === "earnings" ? filteredPayTypes.length : filteredDeductions.length} items
          </span>
        </div>
        <div style={{ position: "relative" }}>
          <Search size={13} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: (C as any).faint, pointerEvents: "none" }} />
          <input type="text" placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} style={{ padding: "9px 12px 9px 36px", border: "1.5px solid " + C.ink, borderRadius: 8, fontFamily: FONT, fontSize: 13, color: C.ink, fontWeight: 500, width: 240, outline: "none", background: "#fff" }} />
        </div>
      </div>

      <PayTypesTable
        category={category}
        items={activeTab === "earnings" ? filteredPayTypes : filteredDeductions}
        kebabOpenId={kebabOpenId}
        onRowClick={(item) => openEditDrawer(item, category)}
        onKebabToggle={(id, e) => { e.stopPropagation(); setKebabOpenId(kebabOpenId === id ? null : id); }}
        onEdit={(item) => { setKebabOpenId(null); openEditDrawer(item, category); }}
        onDuplicate={(item) => onDuplicate(item, category)}
        onToggleActive={(item) => onToggleActive(item, category)}
        onDelete={(item) => onKebabDelete(item, category)}
      />

      <PayTypeDrawer
        open={drawerOpen}
        mode={drawerMode}
        category={drawerCategory}
        draft={draft}
        setDraft={setDraft}
        setCategory={setDrawerCategory}
        editingIsDefault={editingIsDefault}
        saving={saving}
        deleting={deleting}
        saveError={saveError}
        onClose={closeDrawer}
        onSave={onSave}
        onRequestDelete={() => setConfirmDelete(true)}
      />

      <DeleteConfirmModal
        open={confirmDelete}
        title={"Delete this " + drawerCategory + "?"}
        message={<>This will permanently remove <strong style={{ color: C.ink }}>{draft.name}</strong> from your catalog. Employees currently assigned to this {drawerCategory} will need to be reassigned. This cannot be undone.</>}
        confirmLabel={"Yes, delete " + drawerCategory}
        deleting={deleting}
        onConfirm={onDelete}
        onCancel={() => setConfirmDelete(false)}
      />

      <div style={{ marginTop: 24, padding: "18px 22px", background: "#fff", border: "1px solid " + C.line, borderRadius: 10, display: "flex", alignItems: "flex-start", gap: 14, fontSize: 12.5, color: C.ink, lineHeight: 1.6 }}>
        <div style={{ width: 32, height: 32, borderRadius: 8, background: (C as any).surface2 || "#F4F6F8", color: "#2A3F45", display: "grid", placeItems: "center", flex: "0 0 32px" }}>
          <Shield size={16} strokeWidth={1.8} />
        </div>
        <div>
          <strong style={{ color: C.ink, fontWeight: 600, display: "block", marginBottom: 2 }}>How tax treatment drives your payroll</strong>
          Each item's tax flags tell Novala which earnings count toward CPP, EI, and federal tax, and which deductions reduce taxable income. Default settings match current CRA rules. Add and edit coming soon.
        </div>
      </div>
    </div>
  );
}